import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@connectrpc/connect-query'

import type { PhotoRef } from '@jotta/grpc-connect-openapi/photo'
import { Size_Preset } from '@jotta/grpc-connect-openapi/thumb'
import { getThumb } from '@jotta/grpc-connect-openapi/thumbQuery'
import { useFetchedImage } from '@jotta/photo-editor/hooks/useFetchedImage'

export interface PhotoLargeThumb {
  /** Object URL backed by the thumbnail bytes — safe to feed `<img>`. */
  url: string | undefined
  /** Decoded HTMLImageElement — ready for the editor canvas. */
  image: HTMLImageElement | undefined
  /** Raw thumbnail bytes — kept for EXIF/metadata passthrough on save. */
  bytes: Uint8Array | undefined
  /** True while the gRPC fetch or image decode is in flight. */
  isLoading: boolean
  /** Set when the thumbnail fetch failed. */
  error?: unknown
}

export interface PhotoLargeThumbOptions {
  /** When false nothing is fetched, but an already-cached thumbnail is still returned. */
  enabled?: boolean
  /** Last-resort already-located thumbnail URL from the photo asset. */
  fallbackUrl?: string
}

// Object URLs are shared by every consumer of the same cached thumbnail and evicted
// oldest-first. The cap comfortably covers the carousel's three slides plus the editor.
const maxCachedObjectUrls = 24
const objectUrls = new Map<Uint8Array, string>()

function getObjectUrl(bytes: Uint8Array) {
  const cached = objectUrls.get(bytes)
  if (cached) {
    objectUrls.delete(bytes)
    objectUrls.set(bytes, cached)
    return cached
  }
  const url = URL.createObjectURL(
    new Blob([new Uint8Array(bytes)], { type: 'image/jpeg' }),
  )
  objectUrls.set(bytes, url)
  if (objectUrls.size > maxCachedObjectUrls) {
    const [oldestBytes, oldestUrl] = objectUrls.entries().next().value!
    objectUrls.delete(oldestBytes)
    URL.revokeObjectURL(oldestUrl)
  }
  return url
}

/**
 * Fetch the LARGE thumbnail for a photo and expose it as a decoded
 * `HTMLImageElement` (for the editor canvas), an Object URL (for `<img>`
 * display), and raw bytes (for EXIF passthrough). React Query dedupes by
 * (RPC, args), so consumers sharing the same `ref` share a single gRPC
 * fetch.
 */
export function usePhotoLargeThumb(
  ref: PhotoRef | undefined,
  options?: PhotoLargeThumbOptions,
): PhotoLargeThumb {
  const { data, isLoading, error } = useQuery(
    getThumb,
    {
      ref: ref ? { case: 'photoRef', value: ref } : undefined,
      size: { size: { case: 'preset', value: Size_Preset.LARGE } },
    },
    {
      refetchOnMount: false,
      staleTime: 'static',
      enabled: Boolean(ref) && (options?.enabled ?? true),
    },
  )

  const responseBytes =
    data?.response?.case === 'found' ? data.response.value.data : undefined
  const bytes = useMemo(
    () => (responseBytes ? new Uint8Array(responseBytes) : undefined),
    [responseBytes],
  )
  // Resolved during render so a remounted slide shows a cached thumbnail on its first
  // frame instead of flashing the small preview while an effect creates the URL.
  const url = responseBytes ? getObjectUrl(responseBytes) : undefined

  const responseError = useMemo(() => {
    const responseCase = data?.response?.case
    switch (responseCase) {
      case undefined:
      case 'found':
        return undefined
      case 'notFound':
        return new Error('Photo thumbnail not found')
      case 'notSupported':
        return new Error('Photo thumbnail not supported')
      default:
        return new Error(`Photo thumbnail unavailable: ${String(responseCase)}`)
    }
  }, [data])

  const [decoded, setDecoded] = useState<{
    url: string
    image: HTMLImageElement | undefined
  }>()

  useEffect(() => {
    if (!url) {
      return
    }
    const img = new Image()
    img.crossOrigin = 'anonymous'
    let cancelled = false
    img.addEventListener('load', () => {
      if (!cancelled) {
        setDecoded({ url, image: img })
      }
    })
    img.addEventListener('error', () => {
      if (!cancelled) {
        setDecoded({ url, image: undefined })
      }
    })
    img.src = url
    return () => {
      cancelled = true
    }
  }, [url])
  const decodedCurrent = decoded?.url === url ? decoded : undefined

  // Fallback path: fetch + decode via useFetchedImage.
  const fallbackEnabled = Boolean(options?.fallbackUrl) && !isLoading && !bytes
  const fallback = useFetchedImage(options?.fallbackUrl, {
    enabled: fallbackEnabled,
    mimeType: 'image/jpeg',
  })
  const fallbackIsStarting =
    fallbackEnabled && !fallback.image && !fallback.bytes && !fallback.error

  if (url && bytes) {
    return {
      url,
      image: decodedCurrent?.image,
      bytes,
      isLoading: !decodedCurrent,
      error: undefined,
    }
  }

  if (fallback.image && fallback.bytes) {
    return {
      url: undefined,
      image: fallback.image,
      bytes: fallback.bytes,
      isLoading: false,
      error: undefined,
    }
  }

  return {
    url: undefined,
    image: undefined,
    bytes: undefined,
    isLoading: isLoading || fallback.isLoading || fallbackIsStarting,
    error: fallbackIsStarting
      ? undefined
      : (error ?? responseError ?? fallback.error),
  }
}
